import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  Disc3,
  Radio,
  Layers,
  Bookmark,
  BookmarkCheck,
  ExternalLink,
  ArrowRight,
  CheckCircle2,
  MapPin,
  Phone,
  Globe2,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import PaywallGate from "../components/PaywallGate";

const HUBS = {
  master: {
    icon: Layers,
    overline: "Master Rights",
    h1: "Master Rights",
    accent: "Ownership · Licensing · Synchronization",
    intro:
      "Master rights govern the ownership, exploitation, and licensing of sound recordings — distinct from the underlying composition. OSRR's Master Rights hub indexes the publishers, administrators, and digital aggregators who negotiate, license, and collect on behalf of master rights owners worldwide.",
    coverage: [
      "Ownership and assignment of sound recordings",
      "Synchronization licensing for film, TV, advertising, and games",
      "Master use licenses and sample clearance",
      "Catalog acquisition, M&A, and royalty advances",
      "Print, sheet music, and grand rights administration",
      "Worldwide sub-publishing and master administration",
    ],
    keyTopics: [
      {
        title: "Sync Licensing",
        body: "Master use licenses are required separately from publishing sync licenses. Featured agencies in this hub negotiate placements with film, TV, advertising, and game studios.",
      },
      {
        title: "Catalog Administration",
        body: "Independent and major publishers in this hub administer master catalogs across territories, handling royalty collection, copyright registration, and licensing.",
      },
      {
        title: "Master vs. Composition",
        body: "Every recording has two distinct copyrights — the underlying composition and the master recording. OSRR helps you find the right administrator for each.",
      },
    ],
  },
  digital: {
    icon: Radio,
    overline: "Digital Rights",
    h1: "Digital Musical Rights",
    accent: "Streaming · Downloads · Interactive Services",
    intro:
      "Digital rights cover the explosive ecosystem of streaming, downloads, interactive services, and user-generated content platforms. OSRR's Digital Rights hub maps the agencies that license, monitor, and collect from DSPs, social platforms, and emerging digital channels — including the Mechanical Licensing Collective and global digital aggregators.",
    coverage: [
      "Interactive streaming royalties (Spotify, Apple Music, YouTube Music)",
      "Non-interactive digital performance (SiriusXM, Pandora, webcasters)",
      "On-demand downloads and tethered downloads",
      "User-generated content (YouTube, TikTok, Meta, Snap)",
      "Compulsory mechanical licenses under Section 115 (USA)",
      "Digital sub-publishing and global royalty pipelines",
    ],
    keyTopics: [
      {
        title: "MLC & Section 115",
        body: "The Mechanical Licensing Collective administers blanket digital mechanical licenses in the US under the Music Modernization Act. Crucial for songwriters with works on streaming.",
      },
      {
        title: "DSP Negotiations",
        body: "Aggregators like Merlin negotiate global digital licensing deals on behalf of independent labels and artists with platforms like Spotify, YouTube, TikTok, and Meta.",
      },
      {
        title: "Black Box & Unmatched Royalties",
        body: "Substantial digital royalties go uncollected when works aren't properly registered. Featured services help register works correctly across all DSPs and PROs globally.",
      },
    ],
  },
  recording: {
    icon: Disc3,
    overline: "Recording Rights",
    h1: "Recording Rights",
    accent: "Performers · Neighbouring · Master Recordings",
    intro:
      "Recording rights — also known as neighbouring rights or related rights — protect the performers and producers of sound recordings. OSRR's Recording Rights hub connects performers, session musicians, orchestras, and arrangers to the collection societies that gather their share of broadcast and digital performance income worldwide.",
    coverage: [
      "Neighbouring rights for featured and non-featured performers",
      "Broadcast royalties for radio, television, and public venues",
      "Digital performance royalties for sound recordings",
      "Session musician and side-artist payments",
      "Master rights and producer royalties",
      "Treaty-based reciprocal collection across territories",
    ],
    keyTopics: [
      {
        title: "Featured vs. Non-Featured",
        body: "Both featured artists and session musicians (non-featured performers) are entitled to neighbouring rights royalties. Many composers, arrangers, and orchestral players miss out on these.",
      },
      {
        title: "International Reciprocity",
        body: "Through treaties like Rome Convention and WPPT, neighbouring rights royalties flow between countries via reciprocal agreements between societies like PPL, GVL, ADAMI, and SoundExchange.",
      },
      {
        title: "AFM & Performer Unions",
        body: "Performer unions like the American Federation of Musicians negotiate collective agreements covering recording sessions, secondary use, and re-use payments.",
      },
    ],
  },
};

export default function RightsHub() {
  const { category } = useParams();
  const { user, refreshUser } = useAuth();
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);

  const hub = HUBS[category];
  const saved = new Set(user?.saved_agencies || []);

  useEffect(() => {
    if (!hub) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/agencies", { params: { category } });
        setAgencies(data);
      } finally {
        setLoading(false);
      }
    })();
  }, [category, hub]);

  if (!hub) return <Navigate to="/dashboard" replace />;

  const Icon = hub.icon;

  const toggleSave = async (id) => {
    try {
      const { data } = await api.post("/agencies/save", { agency_id: id });
      toast.success(data.action === "saved" ? "Added to your favorites" : "Removed from favorites");
      await refreshUser();
    } catch {
      toast.error("Action failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#0A1128] pt-28 pb-20" data-testid={`rights-hub-${category}`}>
      <div className="max-w-7xl mx-auto px-6">
        {/* Hero */}
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-14">
          <div className="lg:col-span-8">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#7DA3D9]/25 bg-[#7DA3D9]/5 mb-6">
              <Icon className="w-3.5 h-3.5 text-[#7DA3D9]" />
              <span className="text-[10px] uppercase tracking-[0.25em] text-[#B8D0EE] font-semibold">
                {hub.overline} · Industry Hub
              </span>
            </div>
            <h1 className="font-display text-4xl lg:text-7xl tracking-tight leading-[0.98]">
              {hub.h1}
            </h1>
            <div className="overline mt-4 text-[#B8D0EE]">{hub.accent}</div>
          </div>
          <div className="lg:col-span-4">
            <p className="text-slate-300 leading-relaxed">{hub.intro}</p>
          </div>
        </div>

        {/* Coverage strip */}
        <section className="border-y border-white/5 bg-white/[0.015] -mx-6 px-6 py-12 mb-16">
          <div className="max-w-7xl mx-auto">
            <div className="overline mb-5">What this hub covers</div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
              {hub.coverage.map((item) => (
                <div key={item} className="flex items-start gap-3 text-sm text-slate-200">
                  <CheckCircle2 className="w-4 h-4 text-[#7DA3D9] mt-0.5 flex-shrink-0" />
                  <span className="leading-relaxed">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Key topics */}
        <section className="mb-16" data-testid="hub-key-topics">
          <div className="grid lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-5">
              <div className="overline">The essentials</div>
              <h2 className="font-display text-3xl lg:text-4xl mt-3 leading-[1.1]">
                Critical concepts professionals must understand
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-px bg-white/5 rounded-md overflow-hidden border border-white/5">
            {hub.keyTopics.map((t) => (
              <div key={t.title} className="p-7 bg-[#0A1128] hover:bg-white/[0.025] transition-colors">
                <div className="text-[#7DA3D9] text-xs uppercase tracking-[0.2em] font-semibold">
                  {t.title}
                </div>
                <p className="text-slate-300 mt-3 text-sm leading-relaxed">{t.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Resources */}
        <section data-testid="hub-resources">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="overline">Curated resources</div>
              <h2 className="font-display text-3xl lg:text-4xl mt-3 leading-[1.1]">
                Agencies & administrators in this hub
              </h2>
            </div>
            <Link
              to="/agencies"
              className="text-sm text-[#7DA3D9] hover:text-[#B8D0EE] inline-flex items-center gap-1"
              data-testid="hub-view-all-agencies"
            >
              Full registry <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          {loading ? (
            <div className="overline text-center py-16" data-testid="hub-loading">Loading resources…</div>
          ) : !user?.has_complete_access ? (
            <PaywallGate
              title={`Unlock the ${hub.h1} resource grid`}
              subtitle={`The ${hub.h1.toLowerCase()} agency grid is part of OSRR Complete Access. The hero, coverage, and key topics above are free — unlock to see every administrator and resource in this hub.`}
              ctaLabel="Unlock all hubs — $6.95"
            />
          ) : agencies.length === 0 ? (
            <div className="text-center py-16 text-slate-400">No resources yet.</div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {agencies.map((a) => (
                <ResourceCard
                  key={a.id}
                  agency={a}
                  saved={saved.has(a.id)}
                  onToggleSave={() => toggleSave(a.id)}
                />
              ))}
            </div>
          )}
        </section>

        {/* Cross-link */}
        <div className="mt-16 glass-card p-7 flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="overline">Other hubs</div>
            <p className="text-slate-300 mt-2">
              Each rights category needs its own administration partners.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {Object.entries(HUBS)
              .filter(([k]) => k !== category)
              .map(([k, h]) => {
                const HIcon = h.icon;
                return (
                  <Link
                    key={k}
                    to={`/rights/${k}`}
                    className="btn-ghost-gold text-sm inline-flex items-center gap-2"
                    data-testid={`cross-link-${k}`}
                  >
                    <HIcon className="w-4 h-4" /> {h.h1}
                  </Link>
                );
              })}
          </div>
        </div>
      </div>
    </div>
  );
}

function ResourceCard({ agency, saved, onToggleSave }) {
  return (
    <div
      className="glass-card p-7 flex flex-col fade-up"
      data-testid={`hub-resource-${agency.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-3xl">{agency.flag || "🌐"}</div>
        <button
          onClick={onToggleSave}
          className="text-slate-400 hover:text-[#7DA3D9] transition-colors p-1"
          aria-label="Save to Favorites"
          data-testid={`hub-save-${agency.id}`}
        >
          {saved ? <BookmarkCheck className="w-5 h-5 text-[#7DA3D9]" /> : <Bookmark className="w-5 h-5" />}
        </button>
      </div>
      <Link to={`/agencies/${agency.id}`} className="mt-4 group">
        <h3 className="font-display text-2xl group-hover:text-[#B8D0EE] transition-colors">{agency.name}</h3>
        <div className="text-sm text-slate-400 mt-1">{agency.country} · Est. {agency.founded}</div>
      </Link>
      <p className="text-sm text-slate-300 mt-4 leading-relaxed flex-1 line-clamp-3">{agency.description}</p>

      <div className="mt-4 space-y-1.5 text-xs text-slate-400">
        {agency.address && (
          <div className="flex items-start gap-1.5">
            <MapPin className="w-3 h-3 text-[#7DA3D9] mt-0.5 flex-shrink-0" />
            <span className="line-clamp-2">{agency.address}</span>
          </div>
        )}
        {agency.phone && (
          <div className="flex items-center gap-1.5">
            <Phone className="w-3 h-3 text-[#7DA3D9] flex-shrink-0" />
            <span>{agency.phone}</span>
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mt-4">
        {(agency.rights || []).map((r) => (
          <span key={r} className="px-2 py-0.5 rounded-full text-[10px] border border-white/10 bg-white/5 uppercase tracking-wider">
            {r}
          </span>
        ))}
      </div>
      <div className="divider-gold my-5" />
      <div className="flex items-center justify-between text-sm">
        <Link to={`/agencies/${agency.id}`} className="text-[#7DA3D9] hover:text-[#B8D0EE]" data-testid={`hub-view-${agency.id}`}>
          View profile →
        </Link>
        <a
          href={agency.website}
          target="_blank"
          rel="noopener noreferrer"
          className="text-slate-400 hover:text-white inline-flex items-center gap-1.5"
          data-testid={`hub-website-${agency.id}`}
        >
          <Globe2 className="w-3.5 h-3.5" /> Visit
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>
    </div>
  );
}
